import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { API_BASE, api } from '../../../api/client'
import type { PlayerReport } from '../../../api/playerReports'
import { ReportChart } from './ReportChart'
import { PressureEvidence, type PressureEvidenceData } from './PressureEvidence'
import {InferencePanel,type ModelFit} from './InferencePanel'
import {RespirationLine,type RespirationRef} from './RespirationLine'

type Point = { t_s: number; value: number }
type Recovery = {
  baseline_bpm: number | null
  response_bpm: number | null
  fraction_recovered: number | null
  residual_bpm: number | null
  t50_s: number | null
  return_time_s: number | null
  censor_time_s: number | null
  censored: boolean
  status: string
  reasons: string[]
  curve: Point[]
  tolerance_bpm: number | null
}
type GoldTrial = {
  id: string
  mechanic: string
  condition: string
  status: string
  reasons: string[]
  hr_recovery: Recovery | null
}
type GoldSession = {
  sensitivity?: {status:string;value:number|null;unit?:string;n_doses:number;reason?:string|null;curve:Array<{pressure_up:number;difficulty_up:number;value:number}>}
  pressure_regulation_observational?: Array<{block_id:string;pressure_up:number|null;value:number|null;status:string;reasons:string[];early?:{start_s:number;end_s:number;n:number;coverage:number;value:number|null};late?:{start_s:number;end_s:number;n:number;coverage:number;value:number|null};respiration_early?:RespirationRef;respiration_late?:RespirationRef}>
  respiration?: {method:string;label:string;summary:{n_estimated:number;n_ambiguous:number;n_not_estimable:number}}
  historical_pressure?: {mapping_id:string;source:{source:string|null;support_max_s:number|null};trials:Array<{pressure_up:number|null;difficulty_up:number|null;status:string}>}
  pressure_regulation?: Array<{exposure_id?: string;value: number|null;status:string;reasons:string[];note?:string;epochs?:Array<{start_s:number;end_s:number;n:number;coverage:number;median_bpm:number|null}>}>
  recovery_groups?: Array<{
    label: string
    mechanic: string
    value: number | null
    n: number
    n_total: number
    stratum: Record<string, string | number | null>
  }>
  session_id: string
  date: string | null
  version: string
  trial_metrics: GoldTrial[]
  contrasts: Array<{
    label: string
    mechanic: string
    value: number | null
    unit: string
    status: string
    reasons: string[]
  }>
  quality: { reasons: string[]; baseline_context: string; hr_samples: number }
  method_config: Record<string, number|string>
}
export type Indicator = {
  id: string
  title: string
  state: string
  state_label: string
  value: number | null
  unit: string
  n: number
  n_unit?: string
  stratum?: Record<string, unknown> | null
  inference?: ModelFit
  respiration_context?: {status:string;label:string|null;early:RespirationRef|null;late:RespirationRef|null;reason?:string|null} | null
  note: string
  reference?: {
    status: string
    n_people: number
    n_sessions: number
    q05?: number | null
    q25?: number | null
    q50?: number | null
    q75?: number | null
    q95?: number | null
  }
}
type GoldReport = PlayerReport & {
  gold?: { sessions: GoldSession[]; note: string }
  analytics?: { indicators: Indicator[]; rules_version: string; pressure_evidence?: PressureEvidenceData[] }
  population?: {as_of: string; sessions: number; accounts: number; reference_current: boolean}
  method_annex?: {
    title: string
    sections: Array<{ title: string; text: string }>
    notes: string[]
  }
}
const fmt = (v: number | null | undefined, suffix = '') =>
  v == null
    ? 'No estimable'
    : `${v.toLocaleString('es-AR', { maximumFractionDigits: 2 })}${suffix}`
const date = (v: string | null) =>
  v ? new Date(v).toLocaleString('es-AR') : 'Sin fecha'
const sections = [
  ['resumen', 'Resumen'],
  ['fisiologia', 'Reactividad y recuperación'],
  ['practica', 'Decisiones y resultados'],
  ['perfil', 'Cuestionarios'],
  ['metodo', 'Cómo leerlo'],
] as const
const reasons: Record<string, string> = {
  baseline_undershoot_without_observed_band_return: 'La señal pasó por debajo de la referencia, sin una entrada sostenida en banda observada. No se clasifica como no retorno.',
  exposure_stability_not_verified: 'No hay registro continuo que verifique presión y dificultad estables.',
  exposure_receipt_gap: 'Faltan recibos del estado efectivo durante la exposición.',
  exposure_changed: 'Cambió la exposición o la dificultad efectiva.',
  endogenous_feedback: 'El feedback sigue a la fisiología: no acredita exposición independiente y constante.',
  task_schedule_not_verified: 'No se verificó el mismo programa de tareas y fases en ambas ventanas.',
  task_schedule_changed: 'Las tareas o fases no coinciden entre las dos ventanas.',
  task_fidelity_not_verified: 'Los recibos de escena y fases no verifican el programa declarado.',
  incomplete_exposure: 'La exposición no completa el horizonte de observación.',
  insufficient_epoch_signal: 'Las ventanas no tienen cobertura continua suficiente.',
  exposure_fidelity_not_verified: 'No se verificaron todos los canales o hay desviaciones de exposición.',
  ambiguous_exposure_identity: 'Identidad de bloque ausente o duplicada.',
  exposure_interrupted: 'La exposición fue interrumpida.',
  missing_explicit_baseline: 'Falta una referencia inicial explícita.',
  insufficient_fixed_baseline: 'Referencia inicial incompleta.',
  clock_not_verified: 'Alineación temporal no verificada.',
  silver_not_ready: 'Señal pendiente de procesamiento.',
  silver_axis_missing: 'Falta el eje temporal procesado.',
  insufficient_condition_window:
    'La condición no contiene una ventana completa.',
  exposure_not_verified: 'No se pudo verificar la exposición.',
  missing_outcome_anchor: 'Falta el marcador de resultado.',
  response_not_above_baseline_noise:
    'Sin respuesta por encima del ruido: no corresponde calcular recuperación porcentual.',
  incomplete_common_horizon: 'No se observó el horizonte completo.',
  post_result_signal_gap:
    'Hay un hueco de señal que impide localizar el retorno.',
  return_not_observed:
    'Retorno sostenido no observado en el intervalo disponible.',
  insufficient_response_window: 'Respuesta inicial demasiado breve.',
  simulated_or_fabricated: 'Prueba simulada o fabricada, excluida.',
  silver_axis_unreadable: 'El registro de señal no se pudo leer.',
  no_post_result_observation: 'No hay señal posterior al resultado.',
}
const reason = (value: string) => reasons[value] ?? value.replaceAll('_', ' ')

export function GoldReportPage({
  reportPath = '/know/me/reports',
  pdfPath = '/know/me/reports/report.pdf',
  readOnly = false,
}: {
  reportPath?: string
  pdfPath?: string
  readOnly?: boolean
}) {
  const params = useParams()
  const [search, setSearch] = useSearchParams()
  const [data, setData] = useState<GoldReport | null>(null)
  const [error, setError] = useState('')
  const [reload, setReload] = useState(0)
  const [tab, setTab] = useState('resumen')
  const [sessionId, setSessionId] = useState('')
  const [trialId, setTrialId] = useState('')
  const [from, setFrom] = useState(search.get('date_from') ?? '')
  const [to, setTo] = useState(search.get('date_to') ?? '')
  const query = new URLSearchParams(search)
  if (params.sessionId) query.set('session_id', params.sessionId)
  if (params.trialId) query.set('trial_id', params.trialId)
  const qs = query.toString()
  useEffect(() => {
    let active = true
    setData(null)
    setError('')
    api
      .get<GoldReport>(`${reportPath}${qs ? `?${qs}` : ''}`)
      .then((r) => {
        if (!active) return
        setData(r)
        const ss = r.gold?.sessions ?? []
        const selected = ss[ss.length - 1]
        setSessionId(selected?.session_id ?? '')
        setTrialId(
          params.trialId ??
            selected?.trial_metrics[selected.trial_metrics.length - 1]?.id ??
            '',
        )
      })
      .catch((e) => {
        if (active)
          setError(
            e instanceof Error ? e.message : 'No pudimos cargar el informe.',
          )
      })
    return () => {
      active = false
    }
  }, [reportPath, qs, reload, params.trialId])
  const session = data?.gold?.sessions.find((s) => s.session_id === sessionId)
  const selected =
    session?.trial_metrics.find((t) => t.id === trialId) ??
    session?.trial_metrics[0]
  const filter = (event: React.FormEvent) => {
    event.preventDefault()
    setSearch({
      ...Object.fromEntries(search),
      ...(from ? { date_from: from } : {}),
      ...(to ? { date_to: to } : {}),
    })
    if (!from || !to) {
      const next = new URLSearchParams(search)
      from ? next.set('date_from', from) : next.delete('date_from')
      to ? next.set('date_to', to) : next.delete('date_to')
      setSearch(next)
    }
  }
  return (
    <article className="mx-auto w-full max-w-6xl min-w-0 p-md md:p-lg space-y-lg">
      <header className="flex flex-wrap items-start justify-between gap-md">
        <div>
          <Link
            className="text-sm text-text-muted"
            to={readOnly ? '/club' : '/data'}
          >
            Volver
          </Link>
          <h1 className="text-2xl font-semibold">
            {data ? `${data.player} · ${data.title}` : 'Informe de práctica'}
          </h1>
          {data && (
            <p className="text-sm text-text-muted">
              {data.total_sessions} sesiones · {data.trial_count} jugadas ·
              registro cardíaco {data.signal_count}/{data.trial_count}
            </p>
          )}
        </div>
        <a
          className="btn-primary"
          href={`${API_BASE}${pdfPath}${qs ? `?${qs}` : ''}`}
        >
          Descargar informe PDF
        </a>
      </header>
      {!params.sessionId && (
        <details open={Boolean(from || to)}>
          <summary className="text-sm cursor-pointer">Filtrar período</summary>
          <form
            className="mt-sm flex flex-wrap items-end gap-sm"
            onSubmit={filter}
          >
            <label className="text-sm">
              Desde
              <input
                aria-label="Desde"
                className="input block mt-xs max-w-full"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </label>
            <label className="text-sm">
              Hasta
              <input
                aria-label="Hasta"
                className="input block mt-xs max-w-full"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </label>
            <button className="btn-ghost" type="submit">
              Aplicar período
            </button>
          </form>
        </details>
      )}
      {error && (
        <div role="alert" className="banner-err">
          {error}
          <button className="btn-ghost" onClick={() => setReload((x) => x + 1)}>
            Reintentar
          </button>
        </div>
      )}
      {!data && !error && (
        <p role="status">Procesando las mediciones y preparando el informe…</p>
      )}
      {data && (
        <>
          {data.truncated && (
            <p className="banner-warn">
              Alcance limitado a las 50 sesiones más recientes del filtro.
            </p>
          )}
          <label className="block sm:hidden text-sm">
            Sección
            <select
              className="input block w-full mt-xs"
              aria-label="Sección del informe"
              value={tab}
              onChange={(e) => setTab(e.target.value)}
            >
              {sections.map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <nav
            aria-label="Secciones del informe"
            className="hidden sm:flex flex-wrap gap-sm"
          >
            {sections.map(([id, label]) => (
              <button
                key={id}
                className={tab === id ? 'btn-primary' : 'btn-ghost'}
                aria-pressed={tab === id}
                onClick={() => setTab(id!)}
              >
                {label}
              </button>
            ))}
          </nav>
          {tab === 'resumen' && (
            <>
              <GoldSummary indicators={data.analytics?.indicators ?? []} />
              {data.population && <p className="text-xs text-text-secondary">Población consultada: {date(data.population.as_of)} · {data.population.sessions} sesiones · {data.population.accounts} cuentas, no personas confirmadas. Referencia {data.population.reference_current ? 'vigente' : 'pendiente de actualizar'}; sin percentiles cuando el estrato es insuficiente.</p>}
              <section className="card">
                <h2 className="font-semibold">Qué podemos observar</h2>
                <p className="text-sm">
                  {data.gold?.note ??
                    'Los datos disponibles y su calidad se muestran por separado.'}
                </p>
                <p className="text-sm mt-sm">
                  La respuesta al aumentar la presión y el retorno posterior son
                  dimensiones distintas. Sin dosis definida no se calcula
                  sensibilidad; sin exposición estable no se afirma regulación
                  sostenida.
                </p>
              </section>
              <section className="space-y-sm">
                <h2 className="font-semibold">
                  Evolución de la recuperación observada
                </h2>
                <Evolution sessions={data.gold?.sessions ?? []} />
              </section>
            </>
          )}
          {tab === 'fisiologia' && (
            <>
              <label className="block text-sm font-medium">
                Sesión
                <select
                  className="input block w-full mt-xs"
                  value={sessionId}
                  onChange={(e) => {
                    setSessionId(e.target.value)
                    setTrialId('')
                  }}
                >
                  {data.gold?.sessions.map((s) => (
                    <option key={s.session_id} value={s.session_id}>
                      {date(s.date)}
                    </option>
                  ))}
                </select>
              </label>
              {!session ? (
                <p className="card">
                  No hay sesiones procesadas en este alcance.
                </p>
              ) : (
                <>
                  <section className="card space-y-sm">
                    <h2 className="font-semibold">Reactividad por condición</h2>
                    <p className="text-sm text-text-muted">
                      Cambio de HR contra referencia inicial fija; no se asume
                      reposo. Condiciones categóricas: no una dosis ni un efecto
                      causal.
                    </p>
                    <Bars
                      labels={session.contrasts.map(
                        (c) => `${c.label} · ${c.mechanic}`,
                      )}
                      values={session.contrasts.map((c) => c.value)}
                      unit="lpm"
                    />
                    {session.contrasts
                      .filter((c) => c.value == null)
                      .map((c, i) => (
                        <p className="text-sm text-text-muted" key={i}>
                          {c.label}: {c.reasons.map(reason).join(' ')}
                        </p>
                      ))}
                  </section>
                  <section className="card space-y-sm">
                    <h2 className="font-semibold">
                      Cuánto volvió a los 10 segundos
                    </h2>
                    <Bars
                      labels={session.trial_metrics.map(
                        (_, i) => `Jugada ${i + 1}`,
                      )}
                      values={session.trial_metrics.map((t) =>
                        t.hr_recovery?.fraction_recovered == null
                          ? null
                          : t.hr_recovery.fraction_recovered * 100,
                      )}
                      unit="%"
                    />
                    <p className="text-sm text-text-muted">
                      Mismo horizonte para todas. Los huecos no son ceros; falta
                      respuesta detectable o una ventana completa. Puede superar
                      100 %.
                    </p>
                  </section>
                  <section className="card space-y-md">
                    <h2 className="font-semibold">
                      Tiempo y trayectoria de recuperación
                    </h2>
                    <label className="block text-sm">
                      Jugada
                      <select
                        className="input block w-full mt-xs"
                        value={selected?.id ?? ''}
                        onChange={(e) => setTrialId(e.target.value)}
                      >
                        {session.trial_metrics.map((t, i) => (
                          <option key={t.id} value={t.id}>
                            Jugada {i + 1} · {t.mechanic} · {t.status}
                          </option>
                        ))}
                      </select>
                    </label>
                    {selected?.hr_recovery ? (
                      <RecoveryPlot recovery={selected.hr_recovery} />
                    ) : (
                      <p>
                        {selected?.reasons.map(reason).join(' ') ||
                          'Sin señal suficiente para estimar recuperación.'}
                      </p>
                    )}
                  </section>
                  <section className="card">
                    <h2 className="font-semibold">
                      Presión reconstruida y respuesta
                    </h2>
                    <p className="text-sm">
                      La presión programada se reconstruye desde los parámetros y
                      su mapeo versionado, no desde HR, R ni el número de nivel.
                      Las asociaciones históricas son observacionales: R, tareas
                      y audio pueden cambiar. No prueban regulación voluntaria.
                    </p>
                    {session.historical_pressure?.trials.length ? <ReportChart chart={{id:'assigned-pressure-progression',title:'Presión y dificultad programadas',unit:'UP / UD',kind:'line',minimum:0,maximum:100,labels:session.historical_pressure.trials.map((_,i)=>`Jugada ${i+1}`),series:[{label:'Presión (UP)',values:session.historical_pressure.trials.map(t=>t.pressure_up)},{label:'Dificultad (UD)',values:session.historical_pressure.trials.map(t=>t.difficulty_up)}],note:'Coordenadas reconstruidas desde el preset y su resolutor de época. Cero es el ancla baja, no ausencia de presión. Los faltantes no son ceros.'}}/> : null}
                    {session.sensitivity?.value != null ? <div className="mt-sm space-y-sm">
                      <p>Sensibilidad observacional: {fmt(session.sensitivity.value,' lpm/100 UP')} · {session.sensitivity.n_doses} dosis</p>
                      <ReportChart chart={{id:'assigned-pressure-response',title:'HR frente a presión programada (observacional)',unit:'lpm',kind:'line',maximum:null,labels:session.sensitivity.curve.map(p=>`${p.pressure_up} UP · ${p.difficulty_up} UD`),series:[{label:'Mediana por condición',values:session.sensitivity.curve.map(p=>p.value)}],note:'La dificultad puede covariar: describe el paquete, no el efecto causal aislado del estrés. Sin extrapolación fuera del rango registrado.'}}/>
                    </div> : <p className="text-sm text-text-muted mt-sm">Sin mapeo o señal suficientes para estimar la asociación por dosis.</p>}
                    <PressureEvidence evidence={data.analytics?.pressure_evidence?.find(e=>e.session_id===session.session_id)} />
                    {(session.pressure_regulation_observational??[]).filter(b=>b.value!=null).map(block=><div className="text-sm mt-sm" key={block.block_id}><p>Cambio HR con presión programada · {block.pressure_up} UP: {fmt(block.value,' lpm')} (temprana menos tardía; observacional).</p>{block.early && block.late && <p className="text-xs text-text-secondary">Temprana [{fmt(block.early.start_s)}, {fmt(block.early.end_s)}) s: {fmt(block.early.value,' lpm')}, n={block.early.n}, cobertura {fmt(block.early.coverage*100,'%')}. Tardía [{fmt(block.late.start_s)}, {fmt(block.late.end_s)}) s: {fmt(block.late.value,' lpm')}, n={block.late.n}, cobertura {fmt(block.late.coverage*100,'%')}.</p>}{session.respiration ? <RespirationLine early={block.respiration_early} late={block.respiration_late} label={session.respiration.label}/> : <p className="text-xs text-text-muted">Respiración estimada (RSA): no calculada en la versión {session.version}.</p>}</div>)}
                    {session.historical_pressure && <p className="text-sm text-text-muted mt-sm">Mapeo: {session.historical_pressure.mapping_id}. Fuente HR: {session.historical_pressure.source.source??'sin señal'}; soporte previo máximo: {session.historical_pressure.source.support_max_s??'sin dato'} s. Una coordenada UP no es estrés psicológico medido.</p>}
                    <details className="mt-sm">
                      <summary className="cursor-pointer text-sm">Ensayo controlado de presión sostenida (separado)</summary>
                    {(session.pressure_regulation ?? []).map((block,i)=><div key={block.exposure_id ?? i} className="mt-sm space-y-sm">
                      <p>{block.exposure_id ? `Bloque ${block.exposure_id}: ` : ''}{fmt(block.value,' lpm')}</p>
                      {block.value == null ? <p className="text-sm text-text-muted">{block.reasons.map(reason).join(' ')}</p> : <>
                        <Bars labels={['Temprana [30,90)s','Tardía [120,180)s']} values={(block.epochs??[]).map(e=>e.median_bpm)} unit="lpm"/>
                        <p className="text-sm text-text-muted">{block.note}</p>
                      </>}
                    </div>)}
                    </details>
                  </section>
                  <details className="card">
                    <summary>Calidad y método</summary>
                    <p className="text-sm break-words mt-sm">
                      {session.quality.reasons.map(reason).join(' ') ||
                        'Eje temporal y referencia inicial disponibles.'}{' '}
                      Muestras HR: {session.quality.hr_samples}. Método:{' '}
                      {session.version} (experimental).
                    </p>
                    <dl className="text-sm">
                      {Object.entries(session.method_config).map(([k, v]) => (
                        <div
                          key={k}
                          className="flex flex-wrap justify-between gap-sm"
                        >
                          <dt>{k}</dt>
                           <dd className="max-w-full break-all">{v}</dd>
                        </div>
                      ))}
                    </dl>
                  </details>
                </>
              )}
            </>
          )}
          {tab === 'practica' && (
            <>
              <GoldSummary indicators={(data.analytics?.indicators??[]).filter(i=>['latency','precision','performance'].includes(i.id))} />
              <details><summary className="cursor-pointer">Registro descriptivo por ejercicio (secundario)</summary>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
                {data.performance.map((p) => (
                  <section className="card" key={p.scenario}>
                    <h2 className="font-semibold">{p.label}</h2>
                    <p className="text-2xl font-semibold">
                      {fmt(p.success_rate, '%')}
                    </p>
                    <p className="text-sm">
                      {p.successes}/{p.resolved} resultados logrados
                    </p>
                    <ul className="text-sm">
                      {p.outcomes.map((o) => (
                        <li key={o.label}>
                          {o.label}: {o.count}
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
              </details>
              {!data.performance.length && (
                <p className="card">Todavía no hay resultados evaluables.</p>
              )}
              <div className="card">
                <h2 className="font-semibold">Próximo foco de revisión</h2>
                {data.evidence?.plan.map((p, i) => (
                  <div key={i} className="mt-sm">
                    <h3 className="font-medium">{p.title}</h3>
                    <p className="text-sm">{p.summary}</p>
                    <p className="text-sm">{p.action}</p>
                  </div>
                ))}
              </div>
              <Link
                className="text-primary"
                to={readOnly ? '/club' : '/data/reports/detail'}
              >
                Detalle de las sesiones y reflexiones
              </Link>
            </>
          )}
          {tab === 'perfil' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
              {data.evidence?.profile.map((p) => (
                <section className="card min-w-0" key={p.code}>
                  <h2 className="font-semibold">
                    {p.code} · {p.title}
                  </h2>
                  <p className="text-sm text-text-muted">
                    {date(p.date)} · {p.availability}
                  </p>
                  {p.total && (
                    <p className="font-semibold">
                      Total: {p.total.raw} ({p.total.min}–{p.total.max})
                    </p>
                  )}
                  {p.dimensions.map((d) => (
                    <div className="mt-md" key={d.key}>
                      <h3 className="text-sm font-medium">{d.label}</h3>
                      <Dumbbell
                        current={d.score?.mean ?? null}
                        previous={d.previous_mean}
                      />
                      <p className="text-sm">
                        Actual {fmt(d.score?.mean)}/4 · anterior{' '}
                        {fmt(d.previous_mean)} · cambio {fmt(d.change)}
                      </p>
                    </div>
                  ))}
                  <p className="mt-md text-xs text-text-muted">{p.note}</p>
                </section>
              ))}
              <p className="text-sm text-text-muted md:col-span-2">
                {data.questionnaire_note}
              </p>
            </div>
          )}
          {tab === 'metodo' && (
            <section className="card space-y-md">
              <h2 className="text-xl font-semibold">
                {data.method_annex?.title ?? 'Cómo leer este informe'}
              </h2>
              {data.method_annex?.sections.map((s) => (
                <section key={s.title}>
                  <h3 className="font-semibold">{s.title}</h3>
                  <p className="text-sm">{s.text}</p>
                </section>
              ))}
              {data.method_annex?.notes.map((n) => (
                <p key={n} className="text-xs text-text-muted break-words">
                  {n}
                </p>
              ))}
              <p className="text-xs">
                El PDF incluye esta guía como anexo de una página.
              </p>
            </section>
          )}
        </>
      )}
    </article>
  )
}

export function GoldSummary({ indicators }: { indicators: Indicator[] }) {
  const physiologyOrder=['control','reactivity','recovery','sensitivity']
  const physiology=physiologyOrder.flatMap(id=>indicators.filter(i=>i.id===id))
  const practice=indicators.filter(i=>!physiologyOrder.includes(i.id))
  const card=(i:Indicator)=>{
    const behavioral=['latency','precision','performance'].includes(i.id)
    const comparable=!behavioral || i.reference?.status==='available'
    return (
        <section className="card min-w-0" key={i.id}>
          <span
            className={
              comparable && i.state === 'green'
                ? 'tag-ok'
                : comparable && i.state === 'red'
                  ? 'tag-err'
                  : comparable && i.state === 'amber'
                    ? 'tag-warn'
                    : 'tag'
            }
          >
            {comparable ? i.state_label : 'Comparación no disponible'}
          </span>
          <h2 className="text-sm font-medium mt-sm">{i.title}</h2>
          <p className="text-2xl font-semibold mt-sm">
            {comparable ? fmt(i.value) : 'Referencia en construcción'}{' '}
            <span className="text-sm font-normal">
              {comparable && i.value != null ? i.unit : ''}
            </span>
          </p>
          <p className="text-xs text-text-muted">n = {i.n}{i.n_unit ? ` ${i.n===1 ? ({bloques:'bloque',ventanas:'ventana',jugadas:'jugada'} as Record<string,string>)[i.n_unit]??i.n_unit : i.n_unit}` : ''}</p>
          {i.inference && <InferencePanel fit={i.inference} compact/>}
          {i.respiration_context && <RespirationLine early={i.respiration_context.early} late={i.respiration_context.late} label={i.respiration_context.label} className="text-xs mt-sm"/>}
          {(behavioral || i.reference?.status==='available') && <Distribution indicator={i} />}
          {!comparable && i.value!=null && <details className="text-xs mt-sm"><summary className="cursor-pointer">Ver registro observado, sin evaluación poblacional</summary><p>{fmt(i.value)} {i.unit} · n = {i.n}. No es un percentil ni una clasificación individual.</p></details>}
          <p className="text-xs text-text-muted mt-sm">{i.note}</p>
        </section>
    )
  }
  return (
    <div className="space-y-lg">
      {physiology.length>0 && <section className="space-y-sm"><h2 className="font-semibold">Regulación y respuesta a la presión</h2><div className="grid grid-cols-1 sm:grid-cols-2 gap-md">{physiology.map(card)}</div></section>}
      {practice.length>0 && <section className="space-y-sm"><h2 className="font-semibold">Decisión y ejecución (secundario)</h2><div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-md">{practice.map(card)}</div></section>}
    </div>
  )
}

function Distribution({ indicator: i }: { indicator: Indicator }) {
  const r: NonNullable<Indicator['reference']> = i.reference ?? {status:'reference_in_construction',n_people:0,n_sessions:0}
  if (
    r.status !== 'available' ||
    r.q05 == null ||
    r.q95 == null ||
    r.q25 == null ||
    r.q75 == null ||
    r.q50 == null
  )
    return (
      <p className="text-xs text-text-muted mt-sm">
        Referencia en construcción · {r.n_people} personas, {r.n_sessions}{' '}
        sesiones
      </p>
    )
  const lo = Math.min(r.q05, i.value ?? r.q05),
    hi = Math.max(r.q95, i.value ?? r.q95),
    x = (v: number) => 10 + ((v - lo) / (hi - lo || 1)) * 280
  return (
    <figure>
      <svg
        viewBox="0 0 300 35"
        role="img"
        aria-label="Posición frente a los cuantiles de la cohorte"
      >
        <line
          x1={x(r.q05)}
          x2={x(r.q95)}
          y1="17"
          y2="17"
          stroke="currentColor"
        />
        <rect
          x={x(r.q25)}
          width={x(r.q75) - x(r.q25)}
          y="10"
          height="14"
          fill="currentColor"
          opacity=".2"
        />
        <line
          x1={x(r.q50)}
          x2={x(r.q50)}
          y1="7"
          y2="27"
          stroke="currentColor"
        />
        {i.value != null && (
          <circle cx={x(i.value)} cy="17" r="4" fill="currentColor" />
        )}
      </svg>
      <figcaption className="text-xs text-text-muted">
        Punto: jugador · caja: mitad central · {r.n_people} personas
      </figcaption>
    </figure>
  )
}

function Bars({
  labels,
  values,
  unit,
}: {
  labels: string[]
  values: Array<number | null>
  unit: string
}) {
  const [expanded, setExpanded] = useState(false),
    finite = values.filter((v): v is number => v != null && Number.isFinite(v)),
    lo = Math.min(0, ...finite),
    hi = Math.max(1, ...finite),
    y = (v: number) => 125 - ((v - lo) / (hi - lo)) * 110
  if (!finite.length)
    return (
      <p className="text-sm text-text-muted">
        No hay valores comparables para graficar. Los faltantes no se dibujan
        como ceros.
      </p>
    )
  const width = Math.max(320, labels.length * 14),
    step = (width - 50) / Math.max(labels.length, 1)
  return (
    <figure className="min-w-0">
      <svg
        className="w-full"
        viewBox={`0 0 ${width} 155`}
        role="img"
        aria-label={`Valores observados en ${unit}; tabla de datos debajo`}
      >
        <line
          x1="38"
          x2={width - 5}
          y1={y(0)}
          y2={y(0)}
          stroke="currentColor"
          opacity=".3"
        />
        <text x="2" y="12" fontSize="10" fill="currentColor">
          {fmt(hi)} {unit}
        </text>
        <text x="2" y="140" fontSize="10" fill="currentColor">
          {fmt(lo)}
        </text>
        {values.map((v, i) =>
          v == null ? null : (
            <rect
              key={i}
              x={42 + i * step}
              y={Math.min(y(v), y(0))}
              width={Math.max(2, step * 0.65)}
              height={Math.max(1, Math.abs(y(v) - y(0)))}
              fill="currentColor"
              opacity=".65"
            />
          ),
        )}
        <text x="40" y="152" fontSize="10" fill="currentColor">
          Orden de condición / jugada · {finite.length}/{values.length} valores
        </text>
      </svg>
      <button
        className="btn-ghost text-xs"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        Ver valores y etiquetas
      </button>
      {expanded && (
        <dl className="text-sm">
          {labels.map((l, i) => (
            <div
              key={i}
              className="flex flex-wrap justify-between gap-sm border-b border-outline py-xs"
            >
              <dt>{l}</dt>
              <dd>{fmt(values[i], ` ${unit}`)}</dd>
            </div>
          ))}
        </dl>
      )}
    </figure>
  )
}

function RecoveryPlot({ recovery: r }: { recovery: Recovery }) {
  const p = r.curve,
    finite = p.filter((v) => Number.isFinite(v.value)),
    lo = Math.min(...finite.map((v) => v.value), r.baseline_bpm ?? 70) - 2,
    hi = Math.max(...finite.map((v) => v.value), r.response_bpm ?? 90) + 2,
    end = Math.max(1, ...p.map((v) => v.t_s)),
    x = (t: number) => 35 + (t / end) * 530,
    y = (v: number) => 170 - ((v - lo) / (hi - lo)) * 140
  let d = ''
  let last = -Infinity
  finite.forEach((v) => {
    d += `${v.t_s - last > 2 ? 'M' : 'L'}${x(v.t_s)},${y(v.value)} `
    last = v.t_s
  })
  return (
    <>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-sm text-sm">
        {[
          ['Referencia fija', fmt(r.baseline_bpm, ' lpm')],
          ['Respuesta', fmt(r.response_bpm, ' lpm')],
          ['T50', fmt(r.t50_s, ' s')],
          ['Retorno sostenido', fmt(r.return_time_s, ' s')],
          [
            'Grado a 10 s',
            fmt(
              r.fraction_recovered == null ? null : r.fraction_recovered * 100,
              '%',
            ),
          ],
          ['Exceso residual', fmt(r.residual_bpm, ' lpm')],
        ].map(([k, v]) => (
          <div key={k}>
            <dt className="text-text-muted">{k}</dt>
            <dd className="font-semibold">{v}</dd>
          </div>
        ))}
      </dl>
      {p.length > 0 && (
        <svg
          className="w-full"
          viewBox="0 0 600 200"
          role="img"
          aria-label="Trayectoria de HR tras el resultado y banda de referencia fija"
        >
          {r.baseline_bpm != null && (
            <>
              <rect
                x="35"
                y={y(r.baseline_bpm + (r.tolerance_bpm ?? 0))}
                width="530"
                height={
                  y(r.baseline_bpm - (r.tolerance_bpm ?? 0)) -
                  y(r.baseline_bpm + (r.tolerance_bpm ?? 0))
                }
                fill="currentColor"
                opacity=".1"
              />
              <line
                x1="35"
                x2="565"
                y1={y(r.baseline_bpm)}
                y2={y(r.baseline_bpm)}
                stroke="currentColor"
                strokeDasharray="5 4"
              />
            </>
          )}
          <path d={d} fill="none" stroke="currentColor" strokeWidth="2" />
          <text x="2" y="22" fill="currentColor" fontSize="12">
            {fmt(hi)} lpm
          </text>
          <text x="2" y="175" fill="currentColor" fontSize="12">
            {fmt(lo)}
          </text>
          <text x="35" y="195" fill="currentColor" fontSize="12">
            0 s · resultado
          </text>
          <text x="500" y="195" fill="currentColor" fontSize="12">
            {fmt(end, ' s')}
          </text>
        </svg>
      )}
      <p className="text-sm">
        {r.return_time_s != null
          ? 'Retorno sostenido observado.'
          : r.censored
            ? `Retorno no observado durante ${fmt(r.censor_time_s, ' s')}. No significa recuperación fallida.`
            : 'Retorno no estimable con la señal disponible.'}{' '}
        {r.reasons.map(reason).join(' ')}
      </p>
      <p className="text-xs text-text-muted">
        Una jugada, n = 1. Ancla: resultado, no retirada verificada del
        estresor. Banda: tolerancia de medición experimental, no rango de salud.
      </p>
    </>
  )
}

function Evolution({ sessions }: { sessions: GoldSession[] }) {
  const key = (stratum: Record<string, string | number | null>) =>
    JSON.stringify(Object.entries(stratum).sort())
  const groups = new Map(
    sessions.flatMap((s) =>
      (s.recovery_groups ?? [])
        .filter((g) =>
          Object.values(g.stratum).every((v) => v != null && v !== 'unknown'),
        )
        .map((g) => [key(g.stratum), g] as const),
    ),
  )
  return (
    <>
      {[...groups].map(([id, group]) => {
        const values = sessions.map((s) => {
          return (
            s.recovery_groups?.find((g) => key(g.stratum) === id)?.value ?? null
          )
        })
        return (
          <section key={id} className="card">
            <h3 className="font-medium">
              {group.mechanic} · {group.label} · grado recuperado a 10 s
            </h3>
            <Bars
              labels={sessions.map((s) => date(s.date))}
              values={values}
              unit="%"
            />
            <p className="text-xs text-text-muted">
              Solo se agrupan tarea, exposición, preset, referencia y método
              compatibles. Los cambios descriptivos no prueban aprendizaje.
            </p>
          </section>
        )
      })}
      {!groups.size && (
        <p className="card text-sm text-text-muted">
          Todavía no hay una trayectoria evaluable. Se necesita señal con
          referencia fija y horizonte completo.
        </p>
      )}
    </>
  )
}

function Dumbbell({
  current,
  previous,
}: {
  current: number | null
  previous: number | null
}) {
  const x = (v: number) => 15 + ((v - 1) / 3) * 270
  return (
    <svg
      viewBox="0 0 300 30"
      className="w-full"
      role="img"
      aria-label={`Media por ítem: anterior ${fmt(previous)}, actual ${fmt(current)}. Escala de 1 a 4, sin cortes clínicos.`}
    >
      <line
        x1="15"
        x2="285"
        y1="12"
        y2="12"
        stroke="currentColor"
        opacity=".2"
      />
      {previous != null && current != null && (
        <line
          x1={x(previous)}
          x2={x(current)}
          y1="12"
          y2="12"
          stroke="currentColor"
          strokeWidth="2"
        />
      )}
      {previous != null && (
        <circle
          cx={x(previous)}
          cy="12"
          r="4"
          fill="none"
          stroke="currentColor"
        />
      )}
      {current != null && (
        <circle cx={x(current)} cy="12" r="5" fill="currentColor" />
      )}
      <text x="15" y="28" fill="currentColor" fontSize="9">
        1
      </text>
      <text x="280" y="28" fill="currentColor" fontSize="9">
        4
      </text>
    </svg>
  )
}
